import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IFilterAngularComp } from 'ag-grid-angular';
import {
  IAfterGuiAttachedParams,
  IDoesFilterPassParams,
  IFilterParams,
} from 'ag-grid-community';

/** ag-grid's own number operators; the model is the one agNumberColumnFilter emits. */
export type NumberFilterOperator =
  | 'equals'
  | 'notEqual'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'inRange'
  | 'blank'
  | 'notBlank';

export interface NumberFilterModel {
  filterType: 'number';
  type: NumberFilterOperator;
  filter?: number;
  filterTo?: number;
}

const ALL_OPERATORS: readonly NumberFilterOperator[] = [
  'equals',
  'notEqual',
  'greaterThan',
  'greaterThanOrEqual',
  'lessThan',
  'lessThanOrEqual',
  'inRange',
  'blank',
  'notBlank',
];

const NO_VALUE_OPERATORS: ReadonlySet<NumberFilterOperator> = new Set([
  'blank',
  'notBlank',
]);

const OPERATOR_LABEL_KEYS: Record<NumberFilterOperator, string> = {
  equals: 'ag-number-filter.option.equals',
  notEqual: 'ag-number-filter.option.not-equal',
  greaterThan: 'ag-number-filter.option.greater-than',
  greaterThanOrEqual: 'ag-number-filter.option.greater-than-or-equal',
  lessThan: 'ag-number-filter.option.less-than',
  lessThanOrEqual: 'ag-number-filter.option.less-than-or-equal',
  inRange: 'ag-number-filter.option.in-range',
  blank: 'ag-number-filter.option.blank',
  notBlank: 'ag-number-filter.option.not-blank',
};

/**
 * The numeric column filter popup, in the app's own controls (WAP-1647). One
 * condition: an operator, a value, a second value for In range, no value for
 * Blank / Not blank. Emits the standard agNumberColumnFilter model so SSRM
 * and the backend translators are untouched. Honours
 * `filterParams.filterOptions` and `filterParams.defaultOption`. The column
 * normaliser (filter-buttons.utils.ts) installs it grid-wide.
 */
@Component({
  selector: 'app-ag-number-filter',
  templateUrl: './ag-number-filter.component.html',
  styleUrls: ['./ag-number-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AgNumberFilterComponent implements IFilterAngularComp {
  private static nextId = 0;
  private readonly translate = inject(TranslateService);
  private params!: IFilterParams;
  private hidePopup: (() => void) | null = null;
  private applied: NumberFilterModel | null = null;
  private defaultOperator: NumberFilterOperator = 'equals';

  readonly uid = `ag-number-filter-${AgNumberFilterComponent.nextId++}`;
  operators: readonly NumberFilterOperator[] = ALL_OPERATORS;
  /** Pending selection shown in the popup; committed on Apply. */
  operator: NumberFilterOperator = 'equals';
  value: number | null = null;
  valueTo: number | null = null;

  agInit(params: IFilterParams): void {
    this.params = params;
    const filterParams = (params.colDef?.filterParams ?? {}) as {
      filterOptions?: unknown[];
      defaultOption?: unknown;
    };
    const allowed = (filterParams.filterOptions ?? []).filter(
      (option): option is NumberFilterOperator =>
        typeof option === 'string' &&
        ALL_OPERATORS.includes(option as NumberFilterOperator),
    );
    this.operators = allowed.length > 0 ? allowed : ALL_OPERATORS;
    const preferred = filterParams.defaultOption as NumberFilterOperator;
    this.defaultOperator = this.operators.includes(preferred)
      ? preferred
      : this.operators[0];
    this.operator = this.defaultOperator;
  }

  get needsValue(): boolean {
    return !NO_VALUE_OPERATORS.has(this.operator);
  }

  get needsSecondValue(): boolean {
    return this.operator === 'inRange';
  }

  get canApply(): boolean {
    let ok = true;
    if (this.needsValue) {
      ok =
        this.value != null && (!this.needsSecondValue || this.valueTo != null);
    }
    return ok;
  }

  isFilterActive(): boolean {
    return this.applied != null;
  }

  // * Only client-side grids call this; SSRM sends the model to the backend.
  doesFilterPass(params: IDoesFilterPassParams): boolean {
    const model = this.applied;
    if (!model) return true;
    const raw = this.params.getValue(params.node);
    const cell = raw == null || raw === '' ? null : Number(raw);
    const from = model.filter ?? 0;
    const to = model.filterTo ?? 0;
    let pass: boolean;
    switch (model.type) {
      case 'blank':
        pass = cell == null;
        break;
      case 'notBlank':
        pass = cell != null;
        break;
      case 'equals':
        pass = cell === from;
        break;
      case 'notEqual':
        pass = cell !== from;
        break;
      case 'greaterThan':
        pass = cell != null && cell > from;
        break;
      case 'greaterThanOrEqual':
        pass = cell != null && cell >= from;
        break;
      case 'lessThan':
        pass = cell != null && cell < from;
        break;
      case 'lessThanOrEqual':
        pass = cell != null && cell <= from;
        break;
      case 'inRange':
        pass = cell != null && cell >= from && cell <= to;
        break;
    }
    return pass;
  }

  getModel(): NumberFilterModel | null {
    return this.applied;
  }

  setModel(model: NumberFilterModel | null): void {
    const type = model?.type;
    let next: NumberFilterModel | null = null;
    if (type && this.operators.includes(type)) {
      if (NO_VALUE_OPERATORS.has(type)) {
        next = { filterType: 'number', type };
      } else {
        const from = toNumber(model?.filter);
        const to = toNumber(model?.filterTo);
        if (from != null && (type !== 'inRange' || to != null)) {
          next =
            type === 'inRange'
              ? {
                  filterType: 'number',
                  type,
                  filter: from,
                  filterTo: to as number,
                }
              : { filterType: 'number', type, filter: from };
        }
      }
    }
    this.applied = next;
    this.syncPendingFromApplied();
  }

  /** Read-only text shown in the floating filter row. */
  getModelAsString(): string {
    const model = this.applied;
    let text = '';
    if (model) {
      text = this.operatorLabel(model.type);
      if (model.type === 'inRange') {
        text += `: ${model.filter} - ${model.filterTo}`;
      } else if (!NO_VALUE_OPERATORS.has(model.type)) {
        text += `: ${model.filter}`;
      }
    }
    return text;
  }

  afterGuiAttached(params?: IAfterGuiAttachedParams): void {
    this.hidePopup = params?.hidePopup ?? null;
    // Reopening discards any uncommitted edits from the previous visit.
    this.syncPendingFromApplied();
  }

  operatorLabel(operator: NumberFilterOperator): string {
    return this.translate.instant(OPERATOR_LABEL_KEYS[operator]);
  }

  onOperatorChange(operator: NumberFilterOperator): void {
    this.operator = operator;
    if (!this.needsValue) {
      this.value = null;
      this.valueTo = null;
    } else if (!this.needsSecondValue) {
      this.valueTo = null;
    }
  }

  apply(): void {
    if (!this.canApply) return;
    let model: NumberFilterModel = {
      filterType: 'number',
      type: this.operator,
    };
    if (this.needsSecondValue) {
      model = {
        ...model,
        filter: this.value as number,
        filterTo: this.valueTo as number,
      };
    } else if (this.needsValue) {
      model = { ...model, filter: this.value as number };
    }
    this.applied = model;
    this.params.filterChangedCallback();
    this.hidePopup?.();
  }

  /**
   * Header-row input (AgNumberFloatingFilterComponent): the typed number with
   * the current single-value operator, or equals when the popup holds In range
   * / Blank / Not blank; an empty box clears the filter.
   */
  applyFromFloating(value: number | null): void {
    const singleValue =
      !NO_VALUE_OPERATORS.has(this.operator) && this.operator !== 'inRange';
    const operator = singleValue ? this.operator : 'equals';
    this.applied =
      value == null
        ? null
        : { filterType: 'number', type: operator, filter: value };
    this.syncPendingFromApplied();
    this.params.filterChangedCallback();
  }

  /** Cancel / Escape: drop the uncommitted edits and close; the applied filter stays. */
  cancel(): void {
    this.syncPendingFromApplied();
    this.hidePopup?.();
  }

  reset(): void {
    const wasActive = this.isFilterActive();
    this.applied = null;
    this.syncPendingFromApplied();
    if (wasActive) {
      this.params.filterChangedCallback();
    }
    this.hidePopup?.();
  }

  private syncPendingFromApplied(): void {
    this.operator = this.applied?.type ?? this.defaultOperator;
    this.value = this.applied?.filter ?? null;
    this.valueTo = this.applied?.filterTo ?? null;
  }
}

/** A finite number from the model, or null (the built-in filter may send strings). */
function toNumber(raw: unknown): number | null {
  const n = raw == null || raw === '' ? NaN : Number(raw);
  return Number.isFinite(n) ? n : null;
}
